import { faroCoreApiClient } from "@api/faro-core-api-client";
import { getCompanyContext } from "@api/core-api-utils";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { RootState } from "@store/store-helper";

/**
 * Fetch list of companies and workspaces of the user
 * Companies are fetched with user role
 * Workspaces are all companies that the user has access to plus the admin panel access
 */
export const fetchCompaniesAndWorkspaces = createAsyncThunk<
  {
    companies: FaroCoreApiTypes.CompanyWithRoleDetails[];
    workspaces: FaroCoreApiTypes.Workspace[];
  },
  void
>("workspace/fetchCompaniesAndWorkspaces", async () => {
  try {
    const [fetchedCompanies, fetchedWorkspaces] = await Promise.all([
      faroCoreApiClient.company.getCompanies(),
      faroCoreApiClient.users.getCurrentUsersWorkspaces(),
    ]);

    return {
      companies: fetchedCompanies,
      workspaces: fetchedWorkspaces?.workspaces ?? [],
    };
  } catch (error) {
    throw new Error(getErrorDisplayMarkup(error));
  }
});

/** Fetches the branding settings of the company from the backend */
export const fetchCompanyBrandingSettings = createAsyncThunk<
  FaroCoreApiTypes.BrandingSettings,
  void,
  {
    state: RootState;
  }
>("sdbCompany/fetchCompanyBrandingSettings", async (_, { getState }) => {
  const {
    sdbCompany: { selectedSdbCompanyId },
  } = getState();

  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetchCompanyBrandingSettings");
  }
  try {
    const data = await faroCoreApiClient.company.getBrandingSettings({
      companyId: selectedSdbCompanyId,
    });
    return data;
  } catch (error) {
    throw new Error(getErrorDisplayMarkup(error));
  }
});

/** Updates the branding settings of the company */
export const updateCompanyBrandingSettings = createAsyncThunk<
  FaroCoreApiTypes.BrandingSettings,
  {
    /** The payload for updating the company branding settings */
    payload: FaroCoreApiTypes.BrandingSettings;
  },
  {
    state: RootState;
  }
>(
  "sdbCompany/updateCompanyBrandingSettings",
  async ({ payload }, { getState }) => {
    const {
      sdbCompany: { selectedSdbCompanyId },
    } = getState();

    if (!selectedSdbCompanyId) {
      throw new Error(
        "No companyId was given to updateCompanyBrandingSettings"
      );
    }
    try {
      await faroCoreApiClient.company.updateBrandingSettings({
        companyId: selectedSdbCompanyId,
        payload,
      });

      // Returning the payload because the response does not contain any information on the settings that were changed
      return payload;
    } catch (error) {
      throw new Error(getErrorDisplayMarkup(error));
    }
  }
);

/** Fetches company context from the backend */
export const fetchCompanyContext = createAsyncThunk<
  FaroCoreApiTypes.GetCompanyContext,
  void,
  {
    state: RootState;
  }
>("sdbCompany/fetchCompanyContext", async (_, { getState }) => {
  const {
    sdbCompany: { selectedSdbCompanyId },
    app: { isDevModeEnabled },
  } = getState();

  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetchCompanyContext");
  }
  try {
    const data = await getCompanyContext(
      selectedSdbCompanyId,
      isDevModeEnabled
    );
    return data;
  } catch (error) {
    throw new Error(getErrorDisplayMarkup(error));
  }
});

/**
 * Fetches the communication settings of the company from the backend.
 * This includes the language to communicate with company and whether they are using Sphere XG as default app or not
 */
export const fetchCompanyCommunicationSettings = createAsyncThunk<
  FaroCoreApiTypes.CompanyCommunicationSettings,
  void,
  {
    state: RootState;
  }
>(
  "sdbCompany/fetchCompanyCommunicationSettings",
  async (_, { getState }) => {
    const {
      sdbCompany: { selectedSdbCompanyId },
    } = getState();

    if (!selectedSdbCompanyId) {
      throw new Error("No companyId was given to fetchCompanyBrandingSettings");
    }
    try {
      const data =
        await faroCoreApiClient.company.getCompanyCommunicationSettings({
          companyId: selectedSdbCompanyId,
        });
      return data;
    } catch (error) {
      throw new Error(getErrorDisplayMarkup(error));
    }
  },
  {
    condition: (_, api) => {
      // Skip fetching if communication settings are already available in the store
      return !api.getState().sdbCompany.communicationSettings;
    },
  }
);

/**
 * Updates the company communication settings of the company.
 * This includes the language to communicate with company and whether they are using Sphere XG as default app or not
 */
export const updateCompanyCommunicationSettings = createAsyncThunk<
  FaroCoreApiTypes.CompanyCommunicationSettings,
  {
    /** The payload for updating the company communication settings */
    payload: FaroCoreApiTypes.CompanyCommunicationSettings;
  },
  {
    state: RootState;
  }
>(
  "sdbCompany/updateCompanyCommunicationSettings",
  async ({ payload }, { getState }) => {
    const {
      sdbCompany: { selectedSdbCompanyId },
    } = getState();

    if (!selectedSdbCompanyId) {
      throw new Error(
        "No companyId was given to updateCompanyCommunicationSettings"
      );
    }
    try {
      await faroCoreApiClient.company.updateCompanyCommunicationSettings({
        companyId: selectedSdbCompanyId,
        payload,
      });

      // Returning the payload because the response does not contain any information on the settings that were changed
      return payload;
    } catch (error) {
      throw new Error(getErrorDisplayMarkup(error));
    }
  }
);

/** Fetches all subscriptions of the company from the backend */
export const fetchCompanySubscriptions = createAsyncThunk<
  FaroCoreApiTypes.GetCompanySubscriptions[],
  {
    /** The query options for filtering the company subscriptions */
    queryOptions?: FaroCoreApiTypes.CompanySubscriptionOptions;
  },
  {
    state: RootState;
  }
>(
  "sdbCompany/fetchCompanySubscriptions",
  async ({ queryOptions }, { getState }) => {
    const {
      sdbCompany: { selectedSdbCompanyId },
    } = getState();

    if (!selectedSdbCompanyId) {
      throw new Error("No companyId was given to fetchCompanySubscriptions");
    }

    try {
      const response = await faroCoreApiClient.company.getCompanySubscriptions({
        companyId: selectedSdbCompanyId,
        options: queryOptions,
      });
      return response;
    } catch (error) {
      throw new Error(getErrorDisplayMarkup(error));
    }
  }
);
